import { ITranslationMap } from '@principle-theorem/practice-migrations/interfaces';
import {
  type ITag,
  type ITreatmentCategory,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  isINamedDocument,
  isSameRef,
  type INamedDocument,
  type IXSLXExport,
  type XSLXCell,
} from '@principle-theorem/shared';
import { type Column } from 'exceljs';
import { type IBaseAppointmentCategory } from '../interfaces';

export enum AppointmentCategoryResourceType {
  TreatmentCategory = 'Treatment Category',
  AppointmentTag = 'Appointment Tag',
}

export type AppointmentCategoryDestination = ITreatmentCategory | ITag;

export interface IAppointmentToTreatmentCategoryXSLX {
  label: string;
  mapTo: string;
}

export const APPOINTMENT_TO_TREATMENT_CATEGORY_HEADERS: AtLeast<
  Column,
  'key' | 'header'
>[] = [
  {
    key: 'label',
    header: 'Label',
  },
  {
    key: 'mapTo',
    header: 'Map To',
  },
];

export class AppointmentToTreatmentCategoryToXSLX
  implements
    IXSLXExport<IBaseAppointmentCategory, IAppointmentToTreatmentCategoryXSLX>
{
  headers = APPOINTMENT_TO_TREATMENT_CATEGORY_HEADERS;

  constructor(
    private _existingMappings: ITranslationMap<AppointmentCategoryDestination>[],
    private _treatmentCategories: INamedDocument<ITreatmentCategory>[],
    private _appointmentTags: INamedDocument<ITag>[]
  ) {}

  translate(
    records: IBaseAppointmentCategory[]
  ): Record<keyof IAppointmentToTreatmentCategoryXSLX, XSLXCell>[] {
    return records.map((record) => {
      const existingRecord = this._existingMappings.find(
        (existingMapping) =>
          existingMapping.sourceIdentifier === record.id.toString()
      );
      const associatedValue = existingRecord?.associatedValue;
      const mappedCategory = isINamedDocument(associatedValue)
        ? this._treatmentCategories.find((category) =>
            isSameRef(category.ref, associatedValue)
          )
        : undefined;

      const options = [
        ...this._treatmentCategories,
        ...this._appointmentTags,
      ].map((category) => category.name);

      return {
        label: { value: record.name },
        mapTo: {
          value: mappedCategory?.name,
          dataValidation: {
            type: 'list',
            formulae: [`"${options.join(',')}"`],
          },
        },
      };
    });
  }
}
